import React from "react";
import { Link, useParams } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    blocks: [
      ["1. Information we collect", "We collect account information (name, email, business details) provided during signup and onboarding, content you generate using our AI tools, and connection tokens for third-party platforms when you choose to connect them. We also collect basic analytics like page views and feature usage."],
      ["2. How we use it", "To operate the platform, generate content on your behalf, train models on aggregated anonymous data (you can opt out), provide customer support, and improve our services. We never sell your personal data."],
      ["3. Sharing", "We share data with the AI providers that power generation (with strict data-handling agreements), payment processors when you purchase a plan, and law enforcement when legally required."],
      ["4. Your rights", "Access, export, correct, or delete your data anytime from Settings. Contact privacy@makeyourbrand.in for assistance. GDPR and DPDP compliant."],
      ["5. Security", "Encrypted in transit (TLS 1.3) and at rest. Connection tokens are encrypted with AES-256. Bcrypt password hashing. Audit logs maintained for sensitive actions."],
      ["6. Contact", "SURAJIT MAJHI HUF — sales@makeyourbrand.in"],
    ],
  },
  terms: {
    title: "Terms & Conditions",
    blocks: [
      ["1. Acceptance", "By creating an account you agree to these Terms. If you don't agree, please don't use the service."],
      ["2. Service", "MakeYourBrand.IN provides AI-powered tools to generate marketing content. AI output is suggested — you remain responsible for what you publish."],
      ["3. Acceptable use", "No illegal content, no impersonation, no spam, no infringement, no extremist or harmful material. We reserve the right to terminate accounts that violate these rules."],
      ["4. Subscriptions", "Plans renew monthly. Cancel anytime — access continues until the period ends. Refunds per our Refund Policy."],
      ["5. AI content ownership", "You own the content you generate. You grant us a license to host and process it to operate the service."],
      ["6. Limitation of liability", "Service is provided 'as is'. We're not liable for indirect or consequential damages. Maximum liability capped at fees paid in the past 12 months."],
      ["7. Changes", "We may update Terms with 30 days notice."],
      ["8. Governing law", "These Terms are governed by the laws of India."],
    ],
  },
  refund: {
    title: "Refund Policy",
    blocks: [
      ["1. 14-day free trial", "Every new account gets a 14-day free trial. No charge during the trial. Cancel anytime."],
      ["2. Monthly plans", "If you're not satisfied within 7 days of your first paid charge, email us for a full refund. After 7 days, refunds are pro-rated based on unused days."],
      ["3. Annual plans", "Annual plans can be cancelled anytime — you'll receive a pro-rated refund minus a 10% admin fee."],
      ["4. AI credits", "Unused AI credits within the current billing period are not refundable in cash but remain usable until the period ends."],
      ["5. How to request", "Email refunds@makeyourbrand.in with your account email and reason. Most refunds are processed within 5 business days."],
    ],
  },
  "data-deletion": {
    title: "User Data Deletion",
    blocks: [
      ["1. Your right to be forgotten", "You can request deletion of all data associated with your MakeYourBrand.IN account at any time. We comply with GDPR Article 17 (right to erasure) and India's DPDP Act."],
      ["2. Self-service deletion", "Sign in → Settings → 'Delete my account'. This permanently removes your business profile, generated content, connected accounts, brand assets, CRM leads, and any cached tokens within 7 days."],
      ["3. Email request", "If you cannot access your account, email privacy@makeyourbrand.in with the email you signed up with. We'll verify ownership and complete deletion within 14 days."],
      ["4. Connected platforms (Meta, Google, etc.)", "When you disconnect Facebook, Instagram, or Google Business Profile from MakeYourBrand.IN, we immediately revoke and delete the stored access tokens. You can additionally revoke our app from your account settings on each platform."],
      ["5. What is NOT deleted", "Anonymised, aggregated analytics that cannot identify you may be retained. Invoices and payment records are retained for 7 years to comply with Indian tax law."],
      ["6. Contact", "Privacy questions: privacy@makeyourbrand.in · Postal address: SURAJIT MAJHI HUF, India."],
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const data = CONTENT[slug];
  if (!data) {
    return (
      <div className="min-h-screen bg-[#05050A] text-white grid place-items-center">
        <div className="glass rounded-2xl p-8 text-center">
          <h1 className="font-heading text-2xl font-black">Page not found</h1>
          <Link to="/" className="brand-gradient-text mt-3 inline-block">Go home →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05050A] text-white grid-bg p-6 lg:p-12" data-testid={`legal-${slug}`}>
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-10">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-lg brand-gradient grid place-items-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <span className="font-heading font-black text-lg tracking-tight">
            MakeYour<span className="brand-gradient-text">Brand.IN</span>
          </span>
        </div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tight mb-2">{data.title}</h1>
        <p className="text-xs uppercase tracking-[0.25em] text-white/40 font-mono mb-10">Effective Feb 2026 · SURAJIT MAJHI HUF</p>

        <div className="space-y-8">
          {data.blocks.map(([h, p], i) => (
            <section key={i}>
              <h2 className="font-heading text-xl font-bold mb-3">{h}</h2>
              <p className="text-white/70 leading-relaxed">{p}</p>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
